import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, BookOpen, FileText, File, CornerDownLeft, X } from 'lucide-react';
import { useStore } from '../store/useStore';
import { cn } from '../lib/utils';

interface SearchResultItem {
  id: string;
  type: 'asignatura' | 'nota' | 'material';
  title: string;
  subtitle?: string;
  detail?: string;
  targetView: 'asignaturas' | 'notas' | 'roadmap';
  targetId?: string;
}

export function CommandPalette() {
  const { asignaturas, notasGenerales, materiales, navigateTo } = useStore();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Escuchar shortcut de teclado Cmd+K o Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      } else if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Construir resultados de búsqueda cruzada
  const results = useMemo<SearchResultItem[]>(() => {
    if (!query.trim()) return [];

    const q = query.toLowerCase().trim();
    const items: SearchResultItem[] = [];

    // 1. Asignaturas (excluyendo papelera)
    asignaturas
      .filter(a => !a.en_papelera)
      .filter(a => a.nombre.toLowerCase().includes(q) || (a.codigo && a.codigo.toLowerCase().includes(q)))
      .slice(0, 5)
      .forEach(a => {
        items.push({
          id: `asig_${a.id}`,
          type: 'asignatura',
          title: a.nombre,
          subtitle: a.codigo ? `Código: ${a.codigo} • ${a.creditos} CR` : `${a.creditos} CR`,
          detail: a.departamento || 'Asignatura',
          targetView: 'asignaturas',
          targetId: a.id,
        });
      });

    // 2. Notas (excluyendo papelera)
    notasGenerales
      .filter(n => !n.en_papelera)
      .filter(n => n.titulo.toLowerCase().includes(q) || n.contenido.toLowerCase().includes(q))
      .slice(0, 5)
      .forEach(n => {
        items.push({
          id: `nota_${n.id}`,
          type: 'nota',
          title: n.titulo || 'Nota sin título',
          subtitle: n.tags && n.tags.length > 0 ? n.tags.map(t => `#${t}`).join(' ') : 'Apunte personal',
          detail: 'Nota',
          targetView: 'notas',
          targetId: n.id,
        });
      });

    // 3. Materiales
    materiales
      .filter(m => m.nombre.toLowerCase().includes(q) || (m.tags && m.tags.toLowerCase().includes(q)))
      .slice(0, 5)
      .forEach(m => {
        items.push({
          id: `mat_${m.id}`,
          type: 'material',
          title: m.nombre,
          subtitle: m.tags ? `Tags: ${m.tags}` : `Archivo ${m.tipo.toUpperCase()}`,
          detail: 'Material de estudio',
          targetView: 'roadmap',
          targetId: m.asignatura_id,
        });
      });

    return items;
  }, [query, asignaturas, notasGenerales, materiales]);

  // Reset del índice al cambiar la búsqueda
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const handleSelectResult = (item: SearchResultItem) => {
    setIsOpen(false);
    if (item.type === 'asignatura') {
      navigateTo({ view: 'asignaturas', asignaturaId: item.targetId });
    } else if (item.type === 'nota') {
      navigateTo({ view: 'notas', asignaturaId: item.targetId });
    } else if (item.type === 'material') {
      navigateTo({ view: 'roadmap', asignaturaId: item.targetId });
    }
  };

  const handleKeyDownMenu = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => (prev - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) {
        handleSelectResult(results[selectedIndex]);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 p-4 bg-slate-900/40 backdrop-blur-xs animate-in fade-in duration-150">
      <div
        className="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-slate-200/80 overflow-hidden flex flex-col animate-in zoom-in-95 duration-150"
        onKeyDown={handleKeyDownMenu}
      >
        {/* Input Header */}
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 bg-slate-50/50">
          <Search className="w-5 h-5 text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar asignaturas, notas o materiales en todo el sistema... (⌘K)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent border-none text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none font-medium"
          />
          <button
            onClick={() => setIsOpen(false)}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Listado de Resultados */}
        <div className="max-h-96 overflow-y-auto p-2 space-y-1">
          {query.trim() !== '' && results.length === 0 && (
            <div className="p-8 text-center text-slate-400 text-xs font-medium">
              No se encontraron coincidencias para "{query}".
            </div>
          )}

          {query.trim() === '' && (
            <div className="p-6 text-center text-slate-400 text-xs font-medium space-y-1">
              <p>Escribe el nombre de una materia, el contenido de un apunte o un archivo.</p>
              <p className="text-[11px] text-slate-400">Navega con flechas ↑ ↓ y presiona Enter para abrir.</p>
            </div>
          )}

          {results.map((item, idx) => {
            const isSelected = idx === selectedIndex;
            const IconComponent = item.type === 'asignatura' ? BookOpen : item.type === 'nota' ? FileText : File;

            return (
              <div
                key={item.id}
                onClick={() => handleSelectResult(item)}
                onMouseEnter={() => setSelectedIndex(idx)}
                className={cn(
                  "flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-150 select-none",
                  isSelected
                    ? "bg-blue-50/80 text-blue-950 border border-blue-100 shadow-2xs"
                    : "hover:bg-slate-50 text-slate-700 border border-transparent"
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cn(
                    "w-8 h-8 rounded-lg flex items-center justify-center shrink-0",
                    item.type === 'asignatura' ? "bg-blue-100 text-blue-700" :
                    item.type === 'nota' ? "bg-slate-100 text-slate-700" : "bg-purple-100 text-purple-700"
                  )}>
                    <IconComponent size={16} />
                  </div>

                  <div className="min-w-0">
                    <h4 className="text-xs font-semibold tracking-tight text-slate-900 truncate">
                      {item.title}
                    </h4>
                    <p className="text-[11px] text-slate-500 font-mono truncate">
                      {item.subtitle}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] font-mono font-medium text-slate-400 uppercase bg-slate-100 px-2 py-0.5 rounded-md">
                    {item.detail}
                  </span>
                  {isSelected && (
                    <CornerDownLeft size={14} className="text-blue-600 animate-in fade-in duration-100" />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer shortcuts */}
        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between text-[11px] text-slate-400 font-mono">
          <div className="flex items-center gap-3">
            <span>↑ ↓ Seleccionar</span>
            <span>↵ Abrir</span>
            <span>ESC Cerrar</span>
          </div>
          <span>Malla</span>
        </div>
      </div>
    </div>
  );
}
